import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppBar, Tag, Btn } from "../components/index.js";
import { Card } from "../components/Card.js";
import { Frame } from "../components/Frame.js";
import { Highlighter } from "../components/Highlighter.js";
import { MarkdownText } from "../components/MarkdownText.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { useSurface } from "../lib/useSurface.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals, mediaUrl } from "../lib/api.js";
import { bboxToFrame } from "../lib/bbox.js";
import { mmss, type StepWithCandidates } from "../lib/demo.js";

/**
 * Digest — the "重要ポイント・ダイジェスト" (30-second skim) view. For each step it shows only
 * the marked phrases (HighlightedText) plus a thumbnail with the highlighter strokes baked on.
 * A learner can scan a whole manual's essentials in seconds; a new hire can review before a shift.
 * Reached from Viewer / Preview via the "重要ポイントだけ" link.
 */
export function Digest() {
  useSurface("console");
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { data, loading, error, offline, reload } = useAsync(() => manuals.get(id), [id]);

  const steps = (data?.steps ?? []) as StepWithCandidates[];

  // Steps that actually carry a mark (phrase or stroke) — the digest only shows the essentials.
  const marked = useMemo(
    () => steps.filter((s) => (s.highlights?.terms?.length ?? 0) > 0 || (s.highlights?.strokes?.length ?? 0) > 0),
    [steps],
  );

  return (
    <div className="shell">
      <AppBar
        back={id ? `/viewer/${id}` : "/library"}
        backLabel={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon name="chevron" size={16} style={{ transform: "rotate(90deg)" }} />
            戻る
          </span>
        }
        compactBrand
      >
        <Tag dot color="var(--hi)">
          重要ポイント
        </Tag>
      </AppBar>

      <main className="doc" style={{ maxWidth: 820, margin: "0 auto", padding: "30px 24px 130px" }}>
        {loading ? (
          <LoadingState label="重要ポイントを読み込み中…" />
        ) : error ? (
          <ErrorState message={error} offline={offline} onRetry={reload} />
        ) : data ? (
          <>
            <div className="kicker reveal">DIGEST — 30秒で要点</div>
            <h1 className="jp display reveal d1" style={{ margin: "10px 0 6px", fontSize: "clamp(28px,5vw,46px)" }}>
              <span className="hl">重要ポイント</span>だけ、ひと目で。
            </h1>
            <p className="jp reveal d1" style={{ color: "var(--muted)", margin: "4px 0 22px" }}>
              {data.title} — マークした手順 {marked.length} / {steps.length} 件
            </p>

            {marked.length === 0 ? (
              <EmptyState icon={<Icon name="ai-review" size={30} />} title="まだ重要ポイントがありません" className="reveal d1">
                Annotate画面のマーカーで重要箇所をなぞるか、「AIで自動マーク」で要点を抽出すると、ここにダイジェストが表示されます。
                <div style={{ marginTop: 16 }}>
                  <Btn variant="hi" icon={<Icon name="pen" />} onClick={() => navigate(`/annotate/${id}`)}>
                    マークを付ける
                  </Btn>
                </div>
              </EmptyState>
            ) : (
              <Card className="reveal d2">
                {marked.map((step, i) => (
                  <article
                    key={step.stepId}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: 20,
                      padding: 22,
                      alignItems: "center",
                      borderBottom: i < marked.length - 1 ? "1px dashed var(--line)" : undefined,
                    }}
                  >
                    <Frame
                      paper
                      src={mediaUrl(step.imageUrl)}
                      cap={step.videoTimecodeMs != null ? mmss(step.videoTimecodeMs) : undefined}
                      bbox={bboxToFrame(step.bbox, step.objectLabel)}
                      style={{ width: 120, aspectRatio: "3/4" }}
                    >
                      {step.highlights?.strokes?.length ? (
                        <Highlighter
                          src={null}
                          value={step.highlights.strokes}
                          readOnly
                          style={{ position: "absolute", inset: 0 }}
                        />
                      ) : null}
                    </Frame>
                    <div>
                      <Tag mono>STEP {String(step.order).padStart(2, "0")}</Tag>
                      <h2 className="jp t-lg" style={{ margin: "8px 0 6px" }}>
                        {step.title}
                      </h2>
                      {step.highlights?.terms?.length ? (
                        <ul style={{ margin: "6px 0 0", paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                          {step.highlights.terms.map((t) => (
                            <li key={t} className="jp" style={{ fontSize: 16, lineHeight: 1.7 }}>
                              <span aria-hidden="true" style={{ color: "var(--hi-deep)", marginRight: 6 }}>
                                <Icon name="ai-review" size={12} />
                              </span>
                              <span className="hl">{t}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <MarkdownText
                          className="jp"
                          style={{ margin: "6px 0 0", color: "var(--muted)" }}
                          text={step.caption}
                          terms={step.highlights?.terms}
                        />
                      )}
                    </div>
                  </article>
                ))}
              </Card>
            )}
          </>
        ) : null}
      </main>

      <div className="actionbar">
        <Btn
          variant="ghost"
          lg
          icon={<Icon name="chevron" size={16} style={{ transform: "rotate(90deg)" }} />}
          onClick={() => navigate(`/viewer/${id}`)}
        >
          手順を見る
        </Btn>
        <span className="grow" />
        <Btn variant="hi" lg icon={<Icon name="pen" />} onClick={() => navigate(`/annotate/${id}`)}>
          マークを編集
        </Btn>
      </div>
    </div>
  );
}
